import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import style from './style.module.scss';

const MIN = 1;
const MAX = 32;
const STEP = 1.25;
const DRAG_THRESHOLD = 5;
const MIN_MARQUEE = 12;
const GESTURE_IDLE_MS = 140;
const REST = { scale: MIN, tx: 0, ty: 0 };

const MapViewContext = createContext({
  gesturing: false,
  viewportEl: null,
  scale: MIN,
});

export const useMapView = () => useContext(MapViewContext);

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const localPt = (el, cx, cy) => {
  const r = el.getBoundingClientRect();
  return { x: cx - r.left, y: cy - r.top };
};
const wheelXY = (e) => {
  let { deltaX: dx, deltaY: dy } = e;
  if (e.deltaMode === 1) { dx *= 16; dy *= 16; }
  if (e.deltaMode === 2) { dx *= 100; dy *= 100; }
  return { dx, dy };
};

const zoomToRect = (viewport, view, rect) => {
  const { width: vw, height: vh } = viewport.getBoundingClientRect();
  const left = Math.min(rect.x0, rect.x1);
  const top = Math.min(rect.y0, rect.y1);
  const w = Math.abs(rect.x1 - rect.x0);
  const h = Math.abs(rect.y1 - rect.y0);
  if (w < MIN_MARQUEE || h < MIN_MARQUEE) return view;

  const { scale, tx, ty } = view;
  const nextScale = clamp(scale * Math.min(vw / w, vh / h), MIN, MAX);
  if (nextScale <= MIN) return REST;

  const ox = vw / 2;
  const oy = vh / 2;
  const cx = left + w / 2;
  const cy = top + h / 2;
  return {
    scale: nextScale,
    tx: -((cx - ox - tx) / scale) * nextScale,
    ty: -((cy - oy - ty) / scale) * nextScale,
  };
};

/**
 * World keeps its intrinsic content size (no width %).
 * Visual size = fit × userScale via CSS transform only — preserves aspect.
 */
const paintWorld = (node, { scale, tx, ty }, fit) => {
  if (!node) return;
  const el = node;
  const s = fit * scale;
  el.style.width = '';
  el.style.transform = `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) scale(${s})`;
};

/**
 * Zoom / pan (Google Maps–style). Content lays out at natural size;
 * we only scale/translate — never squash via percentage width.
 *
 * fit="content" — hug sheet height at rest.
 * fit="contain" — fit sheet into the pane.
 */
const ZoomPanMap = ({ children, fit = 'contain' }) => {
  const hug = fit === 'content';
  const viewportRef = useRef(null);
  const worldRef = useRef(null);
  const dragRef = useRef(null);
  const viewRef = useRef(REST);
  const fitRef = useRef(1);
  const uiRaf = useRef(0);
  const gestureTimer = useRef(0);

  const [uiScale, setUiScale] = useState(MIN);
  const [zoomed, setZoomed] = useState(false);
  const [marquee, setMarquee] = useState(null);
  const [panning, setPanning] = useState(false);
  const [gesturing, setGesturing] = useState(false);
  const [viewportEl, setViewportEl] = useState(null);

  const paint = useCallback((v = viewRef.current) => {
    paintWorld(worldRef.current, v, fitRef.current);
  }, []);

  const syncUi = useCallback(() => {
    if (uiRaf.current) return;
    uiRaf.current = requestAnimationFrame(() => {
      uiRaf.current = 0;
      const { scale } = viewRef.current;
      setUiScale(scale);
      setZoomed(scale > MIN);
    });
  }, []);

  const markGesture = useCallback(() => {
    setGesturing(true);
    if (gestureTimer.current) clearTimeout(gestureTimer.current);
    gestureTimer.current = setTimeout(() => {
      gestureTimer.current = 0;
      setGesturing(false);
    }, GESTURE_IDLE_MS);
  }, []);

  const commit = useCallback((next) => {
    const scale = clamp(next.scale, MIN, MAX);
    const v = scale <= MIN ? REST : { scale, tx: next.tx, ty: next.ty };
    viewRef.current = v;
    paint(v);
    syncUi();
  }, [paint, syncUi]);

  const zoomAt = useCallback((pt, nextScale) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const { width: vw, height: vh } = vp.getBoundingClientRect();
    const { scale, tx, ty } = viewRef.current;
    if (scale < 1e-6) return;
    const ox = vw / 2;
    const oy = vh / 2;
    commit({
      scale: nextScale,
      tx: pt.x - ox - ((pt.x - ox - tx) / scale) * nextScale,
      ty: pt.y - oy - ((pt.y - oy - ty) / scale) * nextScale,
    });
  }, [commit]);

  useLayoutEffect(() => {
    setViewportEl(viewportRef.current);
    paint();
  }, [paint]);

  useLayoutEffect(() => {
    const world = worldRef.current;
    const vp = viewportRef.current;
    if (!world || !vp) return undefined;

    const syncFit = () => {
      const ww = Math.max(world.offsetWidth, 1);
      const wh = Math.max(world.offsetHeight, 1);

      if (hug) {
        const vw = Math.max(vp.clientWidth, 1);
        const next = vw / ww;
        fitRef.current = next;
        if (viewRef.current.scale <= MIN) {
          vp.style.height = `${wh * next}px`;
        }
        paint();
        return;
      }

      vp.style.height = '100%';
      const { clientHeight: vh, clientWidth: vw } = vp;
      const next = Math.min(vw / ww, vh / wh);
      if (Math.abs(next - fitRef.current) > 0.0001) {
        fitRef.current = next;
        paint();
      } else {
        paint();
      }
    };

    syncFit();
    const ro = new ResizeObserver(syncFit);
    ro.observe(world);
    ro.observe(vp);
    window.addEventListener('resize', syncFit);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncFit);
    };
  }, [hug, children, paint]);

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return undefined;

    const onWheel = (e) => {
      e.preventDefault();
      markGesture();
      const { scale, tx, ty } = viewRef.current;
      const pt = localPt(vp, e.clientX, e.clientY);
      const { dx, dy } = wheelXY(e);

      if (e.ctrlKey || e.metaKey) {
        const next = clamp(scale * Math.exp(-dy * 0.01), MIN, MAX);
        if (next <= MIN) commit(REST);
        else zoomAt(pt, next);
        return;
      }
      if (scale <= MIN) return;
      commit({ scale, tx: tx - dx, ty: ty - dy });
    };

    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [commit, zoomAt, markGesture]);

  useEffect(() => () => {
    if (uiRaf.current) cancelAnimationFrame(uiRaf.current);
    if (gestureTimer.current) clearTimeout(gestureTimer.current);
  }, []);

  const zoomBy = (factor) => {
    const vp = viewportRef.current;
    if (!vp) return;
    markGesture();
    const { width: vw, height: vh } = vp.getBoundingClientRect();
    const next = clamp(viewRef.current.scale * factor, MIN, MAX);
    if (next <= MIN) commit(REST);
    else zoomAt({ x: vw / 2, y: vh / 2 }, next);
  };

  const reset = () => {
    commit(REST);
    setMarquee(null);
    setPanning(false);
    setGesturing(false);
    dragRef.current = null;
    if (gestureTimer.current) clearTimeout(gestureTimer.current);
  };

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('[data-crowd-box], [data-map-controls]')) return;
    const pt = localPt(e.currentTarget, e.clientX, e.clientY);
    const { scale, tx, ty } = viewRef.current;
    dragRef.current = {
      mode: scale <= MIN ? 'marquee' : 'pan',
      x0: pt.x,
      y0: pt.y,
      moved: false,
      tx,
      ty,
      id: e.pointerId,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    const pt = localPt(e.currentTarget, e.clientX, e.clientY);
    const dx = pt.x - d.x0;
    const dy = pt.y - d.y0;
    if (!d.moved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
      d.moved = true;
    }
    if (!d.moved) return;

    markGesture();

    if (d.mode === 'marquee') {
      setMarquee({
        x0: d.x0, y0: d.y0, x1: pt.x, y1: pt.y,
      });
      return;
    }

    if (!panning) setPanning(true);
    commit({
      scale: viewRef.current.scale,
      tx: d.tx + dx,
      ty: d.ty + dy,
    });
  };

  const endDrag = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;

    if (d.mode === 'marquee' && d.moved) {
      markGesture();
      const pt = localPt(e.currentTarget, e.clientX, e.clientY);
      commit(zoomToRect(e.currentTarget, viewRef.current, {
        x0: d.x0, y0: d.y0, x1: pt.x, y1: pt.y,
      }));
    }

    setMarquee(null);
    setPanning(false);
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
  };

  const marqueeBox = useMemo(() => {
    if (!marquee) return null;
    return {
      left: Math.min(marquee.x0, marquee.x1),
      top: Math.min(marquee.y0, marquee.y1),
      width: Math.abs(marquee.x1 - marquee.x0),
      height: Math.abs(marquee.y1 - marquee.y0),
    };
  }, [marquee]);

  const mapCtx = useMemo(() => ({
    gesturing,
    viewportEl,
    scale: uiScale,
  }), [gesturing, viewportEl, uiScale]);

  const viewportClass = [
    style.gridMapViewport,
    !hug && style.gridMapViewportFill,
    panning && style.gridMapViewportPanning,
    zoomed && style.gridMapViewportGrab,
    !zoomed && !panning && style.gridMapViewportSelect,
  ].filter(Boolean).join(' ');

  return (
    <MapViewContext.Provider value={mapCtx}>
      <div className={[style.gridMap, !hug && style.gridMapFill].filter(Boolean).join(' ')}>
        <div
          ref={viewportRef}
          className={viewportClass}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <div
            ref={worldRef}
            className={style.gridMapWorld}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%) scale(1)',
              transformOrigin: 'center center',
            }}
          >
            {children}
          </div>

          {marqueeBox ? <div className={style.gridMapMarquee} style={marqueeBox} /> : null}

          <div
            className={style.gridMapToolbar}
            data-map-controls=""
            onPointerDown={(e) => e.stopPropagation()}
          >
            <button type="button" className={style.gridMapBtn} onClick={() => zoomBy(STEP)} disabled={uiScale >= MAX} aria-label="Zoom in" title="Zoom in">+</button>
            <button type="button" className={style.gridMapBtn} onClick={() => zoomBy(1 / STEP)} disabled={!zoomed} aria-label="Zoom out" title="Zoom out">−</button>
            <button type="button" className={style.gridMapBtn} onClick={reset} disabled={!zoomed} aria-label="Reset view" title="Reset view">Reset</button>
            <span className={style.gridMapZoomLabel}>
              {Math.round(uiScale * 100)}
              %
            </span>
          </div>
        </div>
      </div>
    </MapViewContext.Provider>
  );
};

export default ZoomPanMap;
